import Button from '@components/Button'
import ModalAgribank from '@pages/ConfirmPayment/components/Modal/ModalAGR'
import ModalBIDV from '@pages/ConfirmPayment/components/Modal/ModalBIDV'
import ModalPVCB from '@pages/ConfirmPayment/components/Modal/ModalPVCB'
import ModalVCB from '@pages/ConfirmPayment/components/Modal/ModalVCB'
import * as Sentry from '@sentry/browser'
import { NetWorkService, validResponse } from '@utils/API'
import { CODE_SUCCESS, PaymentMethodKey } from '@utils/constan'
import { detectEnvUat, detectWebview } from '@utils/general'
import { Empty, message, Modal, Spin } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Params, useNavigate, useParams } from 'react-router-dom'
import { RootState } from 'store'
import {
  setDevice,
  setEduBillDetail,
  setLoading1,
  setStudenInfor,
  setUtilitySurchargeExemption,
  Type_Initstate,
} from 'store/appSlice'
import {
  BillType,
  IResponeAGRIBANK,
  IResponeICB,
  IResponePVCB,
  IResponeVCB,
  ItemBillResponse,
  ITransactionHistory,
} from '../../types/general'
import ModalICB from '../ConfirmPayment/components/Modal/ModaICB'
import IconBack from './Components/IconBack'
import SectionHeaderTable from './Components/SectionHeaderTable'
import TableCustom from './Components/Table'

export interface DataType {
  key: React.ReactNode
  name: string
  noidung: string
  phaiThu: string
  daThu: string
  mienGiam: string
  conNo: string
  status: string
  ma_sv: string
  expaned: string
  trang_thai: string
  tongThu: string
  hocKy: string
  loaiThu: string
  height?: number | string
  children?: DataType[]
  date_line: string
  kenh_thu: string
  ngay_tao: string
  ngay_thu: string
  hoc_ky_chu: string
  chi_tiet: string
  createdAt?: string
  so_phieu_bao_group?: string
  so_thu_tu_thanh_toan_chung?: number | null
  _id?: string
  qrCode?: string
  nguon_thu_hp?: string
  so_tien_phu_thu_tien_ich?: number | string | null
  disabledSelect?: boolean
}

function getCookieByKey(key: string) {
  const match = document.cookie.match(new RegExp('(^| )' + key + '=([^;]+)'))
  return match ? JSON.parse(decodeURIComponent(match[2])) : null
}

interface PendingBill {
  _id: string
  so_phieu_bao?: string
  ma_sv?: string
}

interface PendingTransaction {
  _id: string
  transactionCode?: string
  TransactionNo?: string
  orderId?: string
  so_phieu_bao?: string
  billCodes?: string[]
  bills?: PendingBill[]
  ma_sv?: string
  studentCode?: string
  studentName?: string
  HoTen?: string
  hoTen?: string
  student?: {
    HoTen?: string
    MaSV?: string
  }
}

interface PendingTransactionRow {
  rowKey: string
  transactionId: string
  transactionCode: string
  billCodes: string
  studentName: string
  maSV: string
}

type PendingTransactionResponseData =
  | PendingTransaction[]
  | {
      data?: PendingTransaction[]
      records?: PendingTransaction[]
      success?: boolean
      message?: string
    }

interface PendingTransactionApiResponse {
  data?: PendingTransactionResponseData
  records?: PendingTransaction[]
  success?: boolean
  message?: string
}

interface CancelPendingTransactionApiResponse {
  data?: {
    success?: boolean
    message?: string
  }
  success?: boolean
  message?: string
}

interface ModalCancelPendingPaymentProps {
  isModalOpen: boolean
  studentInfo: Type_Initstate['studenInfor']
  onCancelled: () => void
  setIsModalOpen: (value: boolean) => void
}

const getTransactionCode = (transaction: PendingTransaction) =>
  transaction.transactionCode || transaction.TransactionNo || transaction.orderId || transaction._id

const getStudentName = (transaction: PendingTransaction, studentInfo?: Type_Initstate['studenInfor']) =>
  transaction.studentName || transaction.HoTen || transaction.hoTen || transaction.student?.HoTen || studentInfo?.HoTen || '-'

const getStudentCode = (
  transaction: PendingTransaction,
  bill?: PendingBill,
  studentInfo?: Type_Initstate['studenInfor'],
) => bill?.ma_sv || transaction.ma_sv || transaction.studentCode || transaction.student?.MaSV || studentInfo?.MaSV || '-'

const getFallbackBillCode = (transaction: PendingTransaction) => {
  const billCodesFromBills = transaction.bills?.map((bill) => bill.so_phieu_bao).filter(Boolean)
  if (billCodesFromBills?.length) {
    return billCodesFromBills.join(', ')
  }

  if (transaction.billCodes?.length) {
    return transaction.billCodes.join(', ')
  }

  return transaction.so_phieu_bao || '-'
}

const getPendingTransactionRecords = (
  responseData?: PendingTransactionResponseData,
  records?: PendingTransaction[],
) => {
  if (Array.isArray(responseData)) {
    return responseData
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data
  }

  if (Array.isArray(responseData?.records)) {
    return responseData.records
  }

  return records ?? []
}

const mapPendingTransactionsToRows = (
  transactions: PendingTransaction[],
  studentInfo?: Type_Initstate['studenInfor'],
) =>
  transactions.map((transaction) => ({
    rowKey: transaction._id,
    transactionId: transaction._id,
    transactionCode: getTransactionCode(transaction),
    billCodes: getFallbackBillCode(transaction),
    studentName: getStudentName(transaction, studentInfo),
    maSV: getStudentCode(transaction, undefined, studentInfo),
  }))

const isPendingTransactionResponseSuccess = (response?: PendingTransactionApiResponse) => {
  if (response?.success === false) {
    return false
  }

  return Array.isArray(response?.data) || response?.data?.success !== false
}

const getPendingTransactionMessage = (response?: PendingTransactionApiResponse) => {
  if (!Array.isArray(response?.data) && response?.data?.message) {
    return response.data.message
  }

  return response?.message
}

const ModalCancelPendingPayment = ({
  isModalOpen,
  studentInfo,
  onCancelled,
  setIsModalOpen,
}: ModalCancelPendingPaymentProps) => {
  const { Get, Post } = NetWorkService
  const params = useParams()

  const [pendingTransactionRows, setPendingTransactionRows] = useState<PendingTransactionRow[]>([])
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const maSV = studentInfo?.MaSV

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  const handleToggleTransaction = (transactionId: string, checked: boolean) => {
    setSelectedTransactionIds((prev) => {
      if (checked) {
        return prev.includes(transactionId) ? prev : [...prev, transactionId]
      }

      return prev.filter((id) => id !== transactionId)
    })
  }

  const handleGetPendingTransactions = async () => {
    if (!maSV) {
      setPendingTransactionRows([])
      return
    }

    try {
      setLoading(true)
      const res = await Get<PendingTransactionApiResponse>({
        url: `/api/organization/${params.orgCode}/school/${params.schoolCode}/pending-transaction/${maSV}`,
      })

      if (res && validResponse(res) && res.code === CODE_SUCCESS && isPendingTransactionResponseSuccess(res.data)) {
        const transactions = getPendingTransactionRecords(res.data?.data, res.data?.records)
        setPendingTransactionRows(mapPendingTransactionsToRows(transactions, studentInfo))
      } else {
        const msgErr =
          res && validResponse(res) ? getPendingTransactionMessage(res.data) || 'Có lỗi xảy ra' : 'Có lỗi xảy ra'
        message.error(msgErr)
      }
    } catch (error) {
      message.error('Có lỗi xảy ra')
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmCancel = async () => {
    if (!maSV || selectedTransactionIds.length === 0) {
      return
    }

    try {
      setSubmitting(true)
      const res = await Post<CancelPendingTransactionApiResponse>({
        url: `/api/organization/${params.orgCode}/school/${params.schoolCode}/cancel-transaction`,
        body: {
          ma_sv: maSV,
          _ids: selectedTransactionIds,
        },
      })

      if (res && validResponse(res) && res.code === CODE_SUCCESS && res.data?.data?.success !== false) {
        message.success('Hủy thanh toán thành công')
        setSelectedTransactionIds([])
        setIsModalOpen(false)
        onCancelled()
      } else {
        const msgErr = res && validResponse(res) ? res.data?.data?.message || res.data?.message : 'Có lỗi xảy ra'
        message.error(msgErr)
      }
    } catch (error) {
      message.error('Có lỗi xảy ra')
      console.log(error)
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (isModalOpen) {
      setSelectedTransactionIds([])
      handleGetPendingTransactions()
    }
  }, [isModalOpen, maSV])

  return (
    <Modal
      title="Hủy giao dịch đang chờ thanh toán"
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={handleConfirmCancel}
      okText="Xác nhận"
      cancelText="Đóng"
      okButtonProps={{
        danger: true,
        disabled: selectedTransactionIds.length === 0,
        loading: submitting,
      }}
      width={900}
    >
      <div className="overflow-x-auto">
        {loading ? (
          <Spin className="flex justify-center py-8" />
        ) : pendingTransactionRows.length ? (
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="bg-[#f5f5f5]">
                <th className="w-[48px] border border-[#f0f0f0] p-3 text-center"></th>
                <th className="border border-[#f0f0f0] p-3 text-left font-[500]">Mã giao dịch</th>
                <th className="border border-[#f0f0f0] p-3 text-left font-[500]">Số phiếu thu</th>
                <th className="border border-[#f0f0f0] p-3 text-left font-[500]">Tên học viên</th>
                <th className="border border-[#f0f0f0] p-3 text-left font-[500]">Mã học viên</th>
              </tr>
            </thead>
            <tbody>
              {pendingTransactionRows.map((row) => (
                <tr key={row.rowKey}>
                  <td className="border border-[#f0f0f0] p-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedTransactionIds.includes(row.transactionId)}
                      onChange={(event) => handleToggleTransaction(row.transactionId, event.target.checked)}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </td>
                  <td className="border border-[#f0f0f0] p-3">{row.transactionCode}</td>
                  <td className="border border-[#f0f0f0] p-3">{row.billCodes}</td>
                  <td className="border border-[#f0f0f0] p-3">{row.studentName}</td>
                  <td className="border border-[#f0f0f0] p-3">{row.maSV}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty />
        )}
      </div>
    </Modal>
  )
}

const Payment = () => {
  const { Get, Post } = NetWorkService
  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useDispatch()
  const appState = useSelector<RootState, Type_Initstate>((state) => state.app)

  const [searchedColumn, setSearchedColumn] = useState<
    | {
        trang_thai: {
          filter: number
          filterType: string
          type: string
        }
      }
    | undefined
  >({
    trang_thai: { filter: 0, filterType: 'text', type: 'eq' },
  })
  const [bill, setBill] = useState<BillType[]>([])
  const [cloneBill, setCloneBill] = useState<BillType[]>([])
  const [isPaid, setIsPaid] = useState(false)
  const [currentValue, setCurrentValue] = useState<string[]>([])
  const [isSkipSelectBill, setIsSkipSelectBill] = useState(false)
  const [checkBillType, setCheckBillType] = useState(false)
  const [billCheck, setBillCheck] = useState<BillType[]>([])
  const [paymentHistorys, setPaymentHistorys] = useState<ITransactionHistory[]>([])
  const [toastMsg, setToastMsg] = useState(false)
  const [checkSoPhieuBao, setCheckSoPhieuBao] = useState(false)
  const [status, setStatus] = useState(4)
  const [reRender, setReRender] = useState(0)
  const [visible, setVisible] = useState(false)
  const [visibleICB, setVisibleICB] = useState(false)
  const [visibleVCB, setVisibleVCB] = useState(false)
  const [visibleAGR, setVisibleAGR] = useState(false)
  const [visiblePVCB, setVisiblePVCB] = useState(false)
  const [visibleCancelPending, setVisibleCancelPending] = useState(false)
  const [imgSrc, setImgSrc] = useState()
  const [imgSrcICB, setImgSrcICB] = useState<IResponeICB | undefined>(undefined)
  const [imgVCB, setImgVCB] = useState<IResponeVCB | undefined>(undefined)
  const [imgSrcAGR, setImgSrcAGR] = useState<IResponeAGRIBANK | undefined>(undefined)
  const [imgSrcPVCB, setImgSrcPVCB] = useState<IResponePVCB | undefined>(undefined)
  const [listSelect, setListSelect] = useState<string[]>([])
  const MIN_VALUE = 10000

  const missingGroupBills = useMemo(() => {
    if (!currentValue.length) return []
    const missing: string[] = []
    bill.forEach((b) => {
      const sttc = b.so_thu_tu_thanh_toan_chung
      if (typeof sttc === 'number' && sttc > 0 && currentValue.includes(b.so_phieu_bao)) {
        bill.forEach((other) => {
          if (
            other.so_thu_tu_thanh_toan_chung === sttc &&
            !currentValue.includes(other.so_phieu_bao) &&
            !missing.includes(other.so_phieu_bao)
          ) {
            missing.push(other.so_phieu_bao)
          }
        })
      }
    })
    return missing
  }, [bill, currentValue])

  const hasMissingGroup = missingGroupBills.length > 0

  const index = useMemo(() => window.location.href.split('/').length, [])
  const secretCode = useMemo(() => window.location.href.split('/')[index - 1], [])

  const handleBack = () => {
    navigate(`${detectEnvUat() ? '/uat' : ''}/search/${params.orgCode}/school/${params.schoolCode}`)
  }

  const handleNavigate = () => {
    const filteredData = bill.filter((item) => !item.so_phieu_bao)
    setCheckSoPhieuBao(!filteredData.length ? false : true)

    if (
      billCheck.reduce((a, b) => a + b.phai_thu, 0) < MIN_VALUE &&
      billCheck.every((e) => e.paymentChannel === PaymentMethodKey.VNPAY)
    ) {
      setToastMsg(true)
      return false
    }

    !filteredData.length &&
      currentValue.length &&
      !checkBillType &&
      !hasMissingGroup &&
      navigate(
        `${detectEnvUat() ? '/uat' : ''}/confirm-payment/organization/${params.orgCode}/school/${
          params.schoolCode
        }/student/${secretCode}`,
        {
          state: {
            currentValue: billCheck,
          },
        },
      )
  }

  useEffect(() => {
    if (isSkipSelectBill) {
      isSkipSelectBill &&
        navigate(
          `${detectEnvUat() ? '/uat' : ''}/confirm-payment/organization/${params.orgCode}/school/${
            params.schoolCode
          }/student/${secretCode}`,
          {
            state: {
              currentValue: bill.filter((o) => o.trang_thai === 0),
            },
          },
        )
    }
  }, [bill])

  const handleGetBillList = async (params: Readonly<Params<string>>) => {
    try {
      dispatch(setLoading1(true))
      let query = '?limit=99999&offset=0'
      if (searchedColumn && Object.keys(searchedColumn).length > 0) {
        for (const key in searchedColumn) {
          const type = searchedColumn[key].type
          const filter = searchedColumn[key].filter
          const filterType = searchedColumn[key].filterType

          switch (filterType) {
            case 'text' || 'number':
              if (key === 'trang_thai') {
                query += `&${key}[${type}]=${filter}`
              }
              if (key === 'trang_thai' && filter === 1) {
                query += '&ngay_thu[sort]=desc'
              }

              if (key !== 'trang_thai') {
                query += `&${key}[${type}]=${filter}`
              }
              break
            default:
              break
          }
        }
      }

      let secretCode = window.location.href.split('/')[index - 1]

      const isWebview = detectWebview().Webview

      dispatch(setDevice(isWebview === 'Yes' ? 'app' : 'web'))
      if (secretCode.split('?').length > 1) {
        secretCode = secretCode.split('?')[secretCode.split('?').length - 2]
      }

      const url = `/api/organization/${params.orgCode}/school/${params.schoolCode}/secretCode/${secretCode}/bill`
      const res: any = await Get<ItemBillResponse>({
        url: url + query,
      })

      const isPendingStatus = searchedColumn?.trang_thai?.filter === 2
      const latestPaymentHistorys = isPendingStatus ? await handleGetPaymentHistory() : paymentHistorys

      if (res && validResponse(res) && res.code === CODE_SUCCESS && res.data.data?.success !== false) {
        const latestBills = res.data.data.data.records.sort((a, b) => a?.stt - b?.stt)
        dispatch(setUtilitySurchargeExemption(res.data.data.utilitySurchargeExemption))
        if (isPendingStatus) {
          handleSetData(latestPaymentHistorys, latestBills)
        } else {
          setPaymentHistorys([])
          setBill(latestBills)
        }
      } else {
        setBill([])
        const msgErr = res && validResponse(res) ? res.data.data.message : 'Có lỗi xảy ra'
        message.error(msgErr)
        Sentry.captureMessage(msgErr)
        // navigate('/error')
      }
    } catch (error) {
      setPaymentHistorys([])
      message.error('Có lỗi xảy ra')
      console.log(error)
      Sentry.captureException(error)
      setBill([])
      // navigate('/error')
    } finally {
      dispatch(setLoading1(false))
    }
  }

  const handleSetData = (paymentHistorysArgs: ITransactionHistory[], bill: BillType[]) => {
    try {
      let trang_thai = undefined
      if (searchedColumn && Object.keys(searchedColumn).length > 0) {
        for (const key in searchedColumn) {
          const filter = searchedColumn[key].filter

          if (key === 'trang_thai') {
            trang_thai = { trang_thai: filter }
          }
        }
      }
      const isPendingStatus = trang_thai?.trang_thai === 2

      let newArr = bill.reduce<BillType[]>((a, b) => {
        const pendingPayment = paymentHistorysArgs.find((e) => {
          const phieuBaoList = [
            ...(e.so_phieu_bao?.split(' ') || []),
            ...(e.bills?.map((bill) => bill.so_phieu_bao) || []),
          ].filter(Boolean)
          return phieuBaoList.includes(b.so_phieu_bao) && e.status === 2
        })

        if (isPendingStatus && !pendingPayment) {
          return a
        }

        const nextBill = pendingPayment
          ? {
              ...b,
              status: pendingPayment.status,
              trang_thai: pendingPayment.status,
              so_phieu_bao_group:
                pendingPayment.bills?.reduce((prev, next) => (prev += ` ${next.so_phieu_bao}`), '') ||
                pendingPayment.so_phieu_bao,
              qrCode: pendingPayment.qrCode,
              vietQRImage: pendingPayment.qrCode,
              paymentChannel: pendingPayment?.paymentChannel ?? '',
              amount: pendingPayment.amount ?? 0,
              transactionCode: pendingPayment.transactionCode ?? '',
            }
          : { ...b }
        a.push(nextBill)
        return a
      }, [])

      if (status !== 1) {
        newArr = newArr.sort((a: any, b: any) => a?.stt - b?.stt)
      }

      setBill(!trang_thai ? newArr : newArr.filter((e) => e.trang_thai === trang_thai.trang_thai))
    } catch (error) {
      console.log(error)
      Sentry.captureException(error)
    }
  }

  const handleGetPaymentHistory = async () => {
    setPaymentHistorys([])
    try {
      if (appState.studenInfor) {
        const res: any = await Get<ItemBillResponse>({
          url: `/api/organization/${params.orgCode}/school/${params.schoolCode}/payment-history/${appState.studenInfor?.MaSV}?status[eq]=2`,
        })

        if (res && validResponse(res) && res.code === CODE_SUCCESS && res.data.data?.success !== false) {
          const nextPaymentHistorys = Array.isArray(res.data.data) ? res.data.data : []
          setPaymentHistorys(nextPaymentHistorys)
          return nextPaymentHistorys
        } else {
          const msgErr = res && validResponse(res) ? res.data.data.message : 'Có lỗi xảy ra'
          message.error(msgErr)
          Sentry.captureMessage(msgErr)
          navigate(`${detectEnvUat() ? '/uat' : ''}/error`)
        }
      }
    } catch (error) {
      message.error('Có lỗi xảy ra')
      Sentry.captureException(error)
      navigate(`${detectEnvUat() ? '/uat' : ''}/error`)
    } finally {
      // dispatch(setLoading1(false))
    }
    return []
  }

  const handleGetImgBIDV = async (transactionCode: string) => {
    try {
      const res: any = await Post<ItemBillResponse>({
        url: `/api/bidv/transaction/${transactionCode}`,
      })

      if (res && validResponse(res) && res.code === CODE_SUCCESS && res.data.data?.success !== false) {
        console.log('res.data.data.vietQRImage', res.data)
        setImgSrc(res.data.data.vietQRImage)
      } else {
        const msgErr = res && validResponse(res) ? res.data.data.message : 'Có lỗi xảy ra'
        message.error(msgErr)
        Sentry.captureMessage(msgErr)
        navigate(`${detectEnvUat() ? '/uat' : ''}/error`)
      }
    } catch (error) {
      message.error('Có lỗi xảy ra')
      Sentry.captureException(error)
      navigate(`${detectEnvUat() ? '/uat' : ''}/error`)
    } finally {
      // dispatch(setLoading1(false))
    }
  }

  const handleGetImgVCB = async (transactionCode: string) => {
    try {
      const res: any = await Get<ItemBillResponse>({
        url: `/api/organization/${params.orgCode}/school/${params.schoolCode}/generate-qr/vcb/${transactionCode}`,
      })

      if (res && validResponse(res) && res.code === CODE_SUCCESS && res.data.data?.success !== false) {
        console.log('res.data.data.vietQRImage', res.data)
        // setImgSrc(res.data.data.vietQRImage)
        const imgVCB = {
          amount: res.data.data.amount,
          qrCodeBase64: res.data.data.qrCodeBase64,
          school: res.data.data.school,
          token: res.data.data.token,
          transactionCode: res.data.data.transactionCode,
        }
        setImgVCB(imgVCB)
      } else {
        const msgErr = res && validResponse(res) ? res.data.data.message : 'Có lỗi xảy ra'
        message.error(msgErr)
        Sentry.captureMessage(msgErr)
        navigate(`${detectEnvUat() ? '/uat' : ''}/error`)
      }
    } catch (error) {
      message.error('Có lỗi xảy ra')
      Sentry.captureException(error)
      navigate(`${detectEnvUat() ? '/uat' : ''}/error`)
    } finally {
      // dispatch(setLoading1(false))
    }
  }
  useEffect(() => {
    setIsPaid(bill.some((e) => e.trang_thai === 0 || e.trang_thai === 2))
  }, [bill])

  useEffect(() => {
    reRender >= 1 && searchedColumn && handleGetBillList(params)
    setReRender(reRender + 1)
  }, [searchedColumn])

  // kiểm tra việc chọn khác loại phiếu thu
  useEffect(() => {
    if (currentValue.length) {
      const filteredData2 = bill.filter((item) => currentValue.includes(item.so_phieu_bao))
      const typeFeeCode = filteredData2[0]?.ma_loai_thu?.typeFeeCode
      setBillCheck(filteredData2)
      setCheckBillType(!filteredData2.every((e) => e?.ma_loai_thu?.typeFeeCode === typeFeeCode))
    } else {
      setBillCheck([])
      setCheckBillType(false)
    }
    setToastMsg(false)
  }, [currentValue])

  useEffect(() => {
    if (appState?.listEdubillDetail.length !== 0) {
      const edubillDetail = appState?.listEdubillDetail.find(
        (e) => e.orgCode === params.orgCode && e.schoolCode === params.schoolCode,
      )
      dispatch(setEduBillDetail(edubillDetail))
    }
  }, [appState?.listEdubillDetail, params.orgCode])

  // lấy tất cả phiếu thu phục vụ cho filter
  const handleGetAllBill = async () => {
    try {
      dispatch(setLoading1(true))
      const url = `/api/organization/${params.orgCode}/school/${params.schoolCode}/secretCode/${secretCode}/bill?limit=99999&offset=0`

      const res: any = await Get<ItemBillResponse>({
        url: url,
      })

      if (res && validResponse(res) && res.code === CODE_SUCCESS && res.data.data?.success !== false) {
        const { records } = res.data.data.data

        const result = records.filter((e) => e.trang_thai === 0 || e.trang_thai === 2)

        setIsSkipSelectBill(result[0]?.is_skip_select_bill || false)

        setBill(records.filter((e) => e.trang_thai === 0).sort((a, b) => a?.stt - b?.stt))
        setCloneBill(records.sort((a, b) => a?.stt - b?.stt))
        !appState?.studenInfor && dispatch(setStudenInfor(res.data.data.student))
        dispatch(setUtilitySurchargeExemption(res.data.data.utilitySurchargeExemption))

        if (records?.some((e) => e?.is_aq_mobile)) {
          navigate(
            `${detectEnvUat() ? '/uat' : ''}/confirm-payment/organization/${params.orgCode}/school/${
              params.schoolCode
            }/student/${secretCode}`,
            {
              state: {
                currentValue: records,
              },
            },
          )
        }
      } else {
        const msgErr = res && validResponse(res) ? res.data.data.message : 'Có lỗi xảy ra'
        message.error(msgErr)
      }
    } catch (error) {
      message.error('Có lỗi xảy ra')
      console.log(error)
      Sentry.captureException(error)
    } finally {
      dispatch(setLoading1(false))
    }
  }

  useEffect(() => {
    handleGetAllBill()
  }, [])

  return (
    <div className="relative overflow-hidden">
      <div>
        {appState.device === 'web' && (
          <>
            <span className="text-[#595959] underline cursor-pointer flex mt-2 mb-3 w-fit" onClick={handleBack}>
              <IconBack /> Tra cứu học phí
            </span>
            {appState.studenInfor && (
              <div className="md:hidden my-2">
                <p>
                  Tên học viên:{' '}
                  <span className="text-[#151515] text-[16px] font-[500] mb-1">{appState.studenInfor?.HoTen}</span>
                </p>
                <p>
                  Mã học viên:{' '}
                  <span className="text-[#151515] text-[16px] font-[500]">{appState.studenInfor?.MaSV}</span>
                </p>
              </div>
            )}
          </>
        )}
        <SectionHeaderTable
          bill={cloneBill}
          setSearchedColumn={setSearchedColumn}
          searchedColumn={searchedColumn}
          setStatus={setStatus}
        />
        <TableCustom
          bills={bill}
          status={status}
          currentValue={currentValue}
          paymentHistorys={paymentHistorys}
          listSelect={listSelect}
          setListSelect={setListSelect}
          setCurrentValue={setCurrentValue}
          setImgSrc={setImgSrc}
          setImgSrcICB={setImgSrcICB}
        />
        {hasMissingGroup && (
          <p className="text-[#FD0606BF] font-[13px] text-right mt-4">
            *Các phiếu thu {missingGroupBills.join(', ')} phải được chọn cùng trong một lần thanh toán
          </p>
        )}
        {toastMsg && checkBillType && (
          <p className="text-[#FD0606BF] font-[13px] text-right mt-4">
            *Vui lòng chỉ thanh toán các giao dịch cùng loại thu
          </p>
        )}
        {toastMsg &&
          !billCheck.every((e) => e.qrCode) &&
          billCheck.every((e) => e.paymentChannel === PaymentMethodKey.VNPAY) && (
            <p className="text-[#FD0606BF] font-[13px] text-right mt-4">
              *Tiếp tục thanh toán không khả dụng cho đối tác thanh toán VNPAY
            </p>
          )}
        {toastMsg &&
          !billCheck.every((e) => e.qrCode) &&
          billCheck.every((e) => e.paymentChannel !== 'VIETCOMBANK') &&
          billCheck.every((e) => e.paymentChannel !== PaymentMethodKey.AGRIBANK) &&
          billCheck.every((e) => e.paymentChannel !== PaymentMethodKey.PVCB) &&
          billCheck.every((e) => e.paymentChannel !== PaymentMethodKey.BIDV) &&
          billCheck.every((e) => e.paymentChannel !== PaymentMethodKey.VIETINBANK) && (
            <p className="text-[#FD0606BF] font-[13px] text-right mt-4">
              *Tính năng hiện chưa phát triển cho kênh thanh toán này
            </p>
          )}
        {false && toastMsg && !listSelect.length && !currentValue.length && (
          <p className="text-[#FD0606BF] font-[13px] text-right mt-4">*Vui lòng chọn phiếu thu để thanh toán</p>
        )}
        {toastMsg &&
          !!currentValue.length &&
          billCheck.reduce((a, b) => a + b.phai_thu, 0) < MIN_VALUE &&
          billCheck.every((e) => e.paymentChannel === PaymentMethodKey.VNPAY) && (
            <p className="text-[#FD0606BF] font-[13px] text-right mt-4">
              *Số tiền giao dịch nhỏ hơn 10.000 đ. Vui lòng liên hệ đóng trực tiếp tại nhà trường
            </p>
          )}
        {checkSoPhieuBao && currentValue.length && (
          <p className="text-[#FD0606BF] font-[13px] text-right mt-4">
            Phiếu thu bị lỗi. Vui lòng liên hệ phòng Quản lý đào tạo để khiếu nại
          </p>
        )}
        <div className={`mt-6 flex justify-end ${appState.device !== 'web' && 'my-6'}`}>
          {isPaid && searchedColumn?.trang_thai?.filter === 2 && (
            <div className="ml-4 rounded-[10px] border border-[#045993] shadow-[#04599333] shadow-md">
              <Button
                width="w-[200px]"
                type="default"
                text="Hủy thanh toán"
                onClick={() => setVisibleCancelPending(true)}
              />
            </div>
          )}
          {isPaid && billCheck.every((e) => e.paymentChannel !== PaymentMethodKey.VNPAY) && (
            <div className="ml-4">
              <Button
                width="w-[200px]"
                disabled={!currentValue.length || hasMissingGroup}
                text={searchedColumn?.trang_thai?.filter !== 2 ? 'Thanh toán' : 'Tiếp tục thanh toán'}
                onClick={() => {
                  setToastMsg(true)
                  if (searchedColumn?.trang_thai?.filter !== 2) {
                    handleNavigate()
                  } else {
                    if (billCheck.length > 0) {
                      const paymentChannel = billCheck.find((e) => e.paymentChannel)
                      // const paymentChannel = localStorage.getItem('paymentChannel')
                      switch (paymentChannel.paymentChannel) {
                        // case PaymentMethodKey.VIETINBANK:
                        //   setVisibleICB(true)
                        //   break

                        // case PaymentMethodKey.BIDV:
                        //   setVisible(true)
                        //   break
                        // case PaymentMethodKey.VCB:
                        //   setVisibleVCB(true)
                        //   break
                        // case PaymentMethodKey.AGRIBANK:
                        //   setVisibleAGR(true)
                        //   break
                        default:
                          break
                      }

                      const transactionData = getCookieByKey(paymentChannel.transactionCode)
                      if (paymentChannel.paymentChannel === 'VIETCOMBANK') {
                        setVisibleVCB(true)

                        // const imgVCB = {
                        //   amount: transactionData?.amount,
                        //   qrCodeBase64: transactionData?.qrCodeBase64,
                        //   school: transactionData?.school,
                        //   token: transactionData?.token,
                        //   transactionCode: transactionData?.transactionCode,
                        // }
                        // setImgVCB(imgVCB)
                        handleGetImgVCB(transactionData?.transactionCode)
                      }
                      if (paymentChannel.paymentChannel === PaymentMethodKey.AGRIBANK) {
                        setVisibleAGR(true)

                        const imgAGR = {
                          qrLink: transactionData?.qrLink,
                          transactionCode: transactionData?.transactionCode,
                          amount: Number(transactionData?.amount),
                          accountName: transactionData?.accountName,
                          virtualAccount: transactionData?.virtualAccount,
                          content: transactionData?.content,
                        }
                        setImgSrcAGR(imgAGR)
                      }
                      if (paymentChannel.paymentChannel === PaymentMethodKey.BIDV) {
                        setVisible(true)
                        // setImgSrc(transactionData?.qrCodeBase64)
                        handleGetImgBIDV(transactionData?.transactionCode)
                      }
                      if (paymentChannel.paymentChannel === PaymentMethodKey.VIETINBANK) {
                        setVisibleICB(true)

                        const imgICB = {
                          transactionCode: transactionData?.transactionCode,
                          billIds: transactionData?.billIds,
                          amount: transactionData?.amount,
                          vietQRImage: transactionData?.vietQRImage,
                          accountNumber: transactionData?.accountNumber,
                          accountName: transactionData?.accountName,
                        }
                        setImgSrcICB(imgICB)
                      }
                      if (paymentChannel.paymentChannel === PaymentMethodKey.PVCB) {
                        setVisiblePVCB(true)

                        const imgPVCB = {
                          transactionCode: transactionData?.transactionCode,
                          alias: transactionData?.alias ?? '',
                          amount: transactionData?.amount,
                          school: transactionData?.school,
                          qrCodeText: transactionData?.qrCodeText,
                          invoiceExpiryTime: transactionData?.invoiceExpiryTime ?? '',
                        }
                        setImgSrcPVCB(imgPVCB)
                      }
                      // billCheck.every((e) => e.qrCode) && billCheck.length && setVisible(true)
                    }
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
      <ModalBIDV setIsModalOpen={setVisible} isModalOpen={visible} img={imgSrc} allowCancel />
      <ModalICB setIsModalOpen={setVisibleICB} isModalOpen={visibleICB} img={imgSrcICB} allowCancel />
      <ModalVCB setIsModalOpen={setVisibleVCB} isModalOpen={visibleVCB} img={imgVCB} allowCancel />
      <ModalAgribank setIsModalOpen={setVisibleAGR} isModalOpen={visibleAGR} img={imgSrcAGR} allowCancel />
      <ModalPVCB setIsModalOpen={setVisiblePVCB} isModalOpen={visiblePVCB} img={imgSrcPVCB} allowCancel />
      <ModalCancelPendingPayment
        isModalOpen={visibleCancelPending}
        setIsModalOpen={setVisibleCancelPending}
        studentInfo={appState.studenInfor}
        onCancelled={() => {
          setCurrentValue([])
          setListSelect([])
          setBill([])
          setBillCheck([])
          setPaymentHistorys([])
          setStatus(0)
          setSearchedColumn((prev) => ({
            ...prev,
            trang_thai: { filter: 0, filterType: 'text', type: 'eq' },
          }))
        }}
      />
    </div>
  )
}

export default Payment
